import { PeerCommand, PeerEvent, PeerKey, P2PDefaultSettings, StatsSettings, Prefix } from '../Utils/Constants';
import Emitter from '../Utils/Emitter';
/**
 * @class
 * @description Peer class, abstraction of all the logic that contains one unique peer used by the p2p client
 * @extends Emitter
 * @exports Peer
 */
export default class Peer extends Emitter {
    /**
     * Constructs peer.
     * @param {peer} peer Peer data.
     */
    constructor(peer) {
        super();
        this._segmentMap = [];
        this._activeDownload = { id: '', data: [], startTime: 0, size: 0 };
        this._lastResponses = [];
        this._lastSent = [];
        this._decoder = new TextDecoder();
        this._identifierShort = Math.round(Math.random() * 65536) - 32768;
        this._downloaded = [];
        this._totalDownloaded = 0;
        this.downloadBw = 0;
        this.id = peer.id;
        this._peer = peer;
        peer.on(PeerEvent.connect, this._onConnect.bind(this));
        peer.on(PeerEvent.close, this._onClose.bind(this));
        peer.on(PeerEvent.error, this._onError.bind(this));
        peer.on(PeerEvent.data, this._onData.bind(this));
    }
    /**
     * Returns if the peer is currently managing an active download.
     * @returns {boolean} If the peer is on an active download.
     * @public
     */
    isDownloading() {
        return !!this._activeDownload.id;
    }
    /**
     * Destroys this peer, uses the same code executed on close peer event.
     * @public
     */
    destroy() {
        this._onClose();
    }
    /**
     * Callback for peer connect event, saves the address and notifies it.
     * @private
     */
    _onConnect() {
        this.emit(PeerEvent.connect, this);
    }
    /**
     * Callback for peer error event, checks if we need to close it.
     * @private
     */
    _onError(errorData) {
        var _a;
        const detail = (_a = errorData === null || errorData === void 0 ? void 0 : errorData.error) === null || _a === void 0 ? void 0 : _a.errorDetail;
        if (detail === 'sctp-failure') {
            this._onClose();
        }
    }
    /**
     * Callback for peer close event, fails the requests.
     * @private
     */
    _onClose() {
        this._endDownload();
        this.emit(PeerEvent.close, this);
    }
    /**
     * Reset values of the download to allow more requests.
     * @private
     */
    _endDownload() {
        this._activeDownload = { id: '', data: [], startTime: 0, size: 0 };
    }
    /**
     * Callback method for peers response.
     * @param {ArrayBuffer} data Message received from the peer.
     * @private
     */
    _onData(data) {
        const header = data.slice(0, 4);
        const myData = data.slice(4, data.byteLength);
        const dataType = header[0];
        if (dataType == Prefix.message) {
            const dataObject = JSON.parse(this._decoder.decode(myData));
            if (dataObject[PeerKey.command]) {
                switch (dataObject[PeerKey.command]) {
                    case PeerCommand.request:
                        this.emit(PeerEvent.segmentRequest, {
                            peer: this,
                            segment: dataObject[PeerKey.id],
                            identifier: dataObject[PeerKey.identfier]
                        });
                        break;
                    case PeerCommand.map:
                        this._segmentMap = dataObject[PeerKey.map];
                        this.emit(PeerEvent.dataUpdated);
                        break;
                    case PeerCommand.cancel:
                        this._cancel = dataObject[PeerKey.id];
                        break;
                    case PeerCommand.absent:
                        if (this._activeDownload.id === dataObject[PeerKey.id]) {
                            this.emit(PeerEvent.segmentAbsent, { peer: this, id: this._activeDownload.id });
                            this._endDownload();
                        }
                        break;
                    case PeerCommand.data:
                        this._activeDownload.size = dataObject[PeerKey.size];
                        this._activeDownload.id = dataObject[PeerKey.id];
                        this._activeDownload.data = [];
                        this._activeDownload.startTime = Date.now();
                        if (this._totalDownloaded >= 3 && this.downloadBw > 0) {
                            const estimatedTime = (this._activeDownload.size * 1000) / this.downloadBw;
                            if (estimatedTime > P2PDefaultSettings.timeout * 1.15) {
                                // add some margin
                                // console.warn("This peer may timeout in " + estimatedTime + "ms");
                            }
                        }
                        break;
                }
            }
        }
        else if (dataType == Prefix.data) {
            if (this._activeDownload.id) {
                this._activeDownload.data.push(myData);
                let totalSize = 0;
                this._activeDownload.data.forEach((segment) => {
                    totalSize += segment.byteLength;
                });
                if (totalSize >= this._activeDownload.size) {
                    let offset = 0;
                    const newData = new Uint8Array(totalSize);
                    for (const fragment of this._activeDownload.data) {
                        newData.set(new Uint8Array(fragment), offset);
                        offset += fragment.byteLength;
                    }
                    const downloadTime = Date.now() - (this._activeDownload.startTime || Date.now());
                    this._activeDownload.startTime = 0;
                    const bandwidth = (1000 * newData.byteLength) / downloadTime;
                    this.downloadBw = Math.trunc(this.downloadBw == 0 ? bandwidth : (this.downloadBw + bandwidth) / 2);
                    this._totalDownloaded++;
                    this._lastResponses.push({
                        time: Date.now(),
                        size: newData.byteLength
                    });
                    this._downloaded.push(this._activeDownload.id);
                    this.emit(PeerEvent.segmentLoaded, {
                        peer: this,
                        id: this._activeDownload.id,
                        time: downloadTime,
                        data: newData
                    });
                    this._endDownload();
                }
            }
        }
    }
    /**
     * Requests a video segment with its id.
     * @param {string} id Id of the segment to request.
     * @public
     */
    request(id) {
        this._startTimeout(id);
        //this._activeDownload.startTime = Date.now();
        this._send({
            [PeerKey.command]: PeerCommand.request,
            [PeerKey.id]: id,
            [PeerKey.identfier]: this._identifierShort
        }, Prefix.message);
    }
    /**
     * Cancel a video segment request with its id.
     * @param {string} id Id of the segment to cancel.
     * @public
     */
    _cancelRequest(id) {
        this._send({
            [PeerKey.command]: PeerCommand.cancel,
            [PeerKey.id]: id,
            [PeerKey.identfier]: this._identifierShort
        }, Prefix.message);
    }
    /**
     * Sends the segment map object.
     * @param {map} map Segment map object.
     * @public
     */
    sendMap(map) {
        this._send({
            [PeerKey.command]: PeerCommand.map,
            [PeerKey.map]: map
        }, Prefix.message);
    }
    /**
     * Given a segment and its id, sends it to the peer split in messages if needed.
     * @param {string} id Id of the segment.
     * @param {VideoSegment} segment The segment to send itself.
     * @param {number} identifier Identifier of the message.
     * @public
     */
    sendSegment(id, segment, identifier) {
        var _a;
        if (segment.data && this._cancel !== id) {
            this._send({
                [PeerKey.command]: PeerCommand.data,
                [PeerKey.id]: id,
                [PeerKey.size]: segment.data.byteLength,
                [PeerKey.identfier]: identifier,
                [PeerKey.splits]: Math.floor(segment.data.byteLength / P2PDefaultSettings.maxMessageSize) + 1
            }, Prefix.message);
            let restBytes = segment.data.byteLength;
            const bufferPrefix = Peer._getBytesFromShort(identifier);
            while (restBytes > 0 && this._cancel !== id) {
                const sendBytes = Math.min(restBytes, P2PDefaultSettings.maxMessageSize);
                this._peer.write(Buffer.concat([bufferPrefix, Buffer.from(segment.data, ((_a = segment.data) === null || _a === void 0 ? void 0 : _a.byteLength) - restBytes, sendBytes)]));
                restBytes -= sendBytes;
            }
            if (this._cancel === id) {
                this.emit(PeerEvent.bytesUploadedDiscarded, segment.data.byteLength - restBytes);
                this._cancel = undefined;
            }
            else {
                this.emit(PeerEvent.bytesUploaded, segment.data.byteLength);
                this._lastSent.push({
                    time: Date.now(),
                    size: segment.data.byteLength
                });
            }
        }
    }
    static _getBytesFromShort(inNumber) {
        const byteN = inNumber < 0 ? inNumber + 32768 : inNumber;
        const byte2 = byteN % 256;
        const temp = (byteN - byte2) / 256;
        const byte1 = inNumber < 0 ? temp - 128 : temp;
        return Buffer.from([Prefix.data, byte1, byte2, 0]);
    }
    /**
     * Sends a message of no segment available to the peer.
     * @param {string} id Id of the segment.
     * @public
     */
    sendNoSegment(id) {
        this._send({
            [PeerKey.command]: PeerCommand.absent,
            [PeerKey.id]: id
        }, Prefix.message);
    }
    /**
     * Sends the given object to the peer.
     * @param {peerMessage} msg Message to send.
     * @param {number} id ID number to send in first bytes of the message.
     * @private
     */
    _send(msg, id) {
        this._peer.write(Buffer.concat([Buffer.from([id, 0, 0, 0]), Buffer.from(JSON.stringify(msg))]));
    }
    /**
     * Returns the list of chunks available of this peer and their statuses.
     * @returns {Object} The segment map of the peer.
     * @public
     */
    getSegmentMap() {
        return this._segmentMap;
    }
    /**
     * Starts the peer timeout for the download.
     * @param {string} id Id of the peer.
     * @private
     */
    _startTimeout(id) {
        this._activeDownload.id = id;
        setTimeout(() => {
            const index = this._downloaded.indexOf(id);
            if (index < 0) {
                this._cancelRequest(id);
                this.emit(PeerEvent.segmentTimeout, { peer: this, id, size: this._activeDownload.size });
                this._endDownload();
            }
            else {
                this._downloaded.splice(index, 1);
            }
        }, P2PDefaultSettings.timeout);
    }
    /**
     * Returns the timestamp of the oldest request stored in the lastResponses object, or 0 if is empty.
     * @returns {number} Timestamp of the oldest stored request.
     * @public
     */
    getOldestRequestTS() {
        var _a;
        return ((_a = this._lastResponses[0]) === null || _a === void 0 ? void 0 : _a.time) || 0;
    }
    /**
     * Returns the bytes downloaded/uploaded from the Peer the last -defined- seconds.
     * Definition in constants: StatsSettings.totalDataInterval
     * @returns {number} Bytes downloaded.
     * @private
     */
    static _getLastSecondsTraffic(reqArray) {
        let accumTraffic = 0;
        const now = Date.now();
        for (let i = 0; i < reqArray.length;) {
            if (reqArray[i].time + StatsSettings.totalDataInterval < now) {
                reqArray.splice(i, 1);
            }
            else {
                accumTraffic += reqArray[i].size;
                ++i;
            }
        }
        return accumTraffic;
    }
    /**
     * Returns the bytes downloaded from the peers the last -defined- seconds. 60 By default.
     * Definition in constants: StatsSettings.totalDataInterval
     * @returns {number} Bytes downloaded.
     * @public
     */
    getLastDownloadsTraffic() {
        return Peer._getLastSecondsTraffic(this._lastResponses);
    }
    /**
     * Returns the bytes uploaded from the peers the last -defined- seconds. 60 By default.
     * Definition in constants: StatsSettings.totalDataInterval
     * @returns {number} Bytes downloaded.
     * @public
     */
    getLastUploadsTraffic() {
        return Peer._getLastSecondsTraffic(this._lastSent);
    }
}
